import { PaymentMethodType } from './payment-types.types';
import { Timestamps, DynamoDBItem, Versioned } from './shared.types';

export enum PurchaseStatus {
  DRAFT = 'DRAFT',
  CLOSED = 'CLOSED',
  PENDING = 'PENDING',
  CANCELLED = 'CANCELLED',
}

export enum PurchaseOrderPaymentStatus {
  UNPAID = 'UNPAID',
  PAID = 'PAID',
  PARTIALLY_PAID = 'PARTIALLY_PAID',
}

export interface CancelPurchaseOrderItemFraction {
  id: string;
  parent_id: string;
  fraction: number;
  cancelled: number;
}

export interface CancelPurchaseOrderItem {
  id: string;
  cancelled: number;
  fractions?: CancelPurchaseOrderItemFraction[];
}

export interface PurchaseOrderPayment extends Timestamps, Versioned {
  id: string;
  purchase_order_id: string;
  amount_paid: number;
  amount_refunded: number;
  payment_location: string;
  payment_method_id?: string;
  payment_method_type?: PaymentMethodType;
  merchant_id: string;
  store_id: string;
  supplier_id: string;
  amount_due: number;
  total_paid: number;
  total_refunded: number;
  total_amount: number;
  final_amount: number;
  creator_id: string;
  creator_name: string;
  refund_note?: string;
  total_cancelled_amount: number;
  shift_id?: string; // TODO: Make Optional // Shift during which the purchase order was created
  /** When this record is a full-reversal of a prior supplier payment, points at that payment's `id`. */
  reverses_payment_id?: string;
  /** Stamped on the original payment when it has been reversed. Prevents double-reversal. */
  reversed_by_payment_id?: string;
  /** Convenience flag set to true when `reverses_payment_id` is populated. */
  is_reversal?: boolean;
}

export interface CreatePurchaseOrderPayment {
  amount_paid: number;
  payment_location: string;
  merchant_id: string;
  purchase_order_id: string;
  payment_method_id: string;
  payment_method_type: PaymentMethodType;
  store_id: string;
  creator_id: string;
  refund_note?: string;
  shift_id?: string; // TODO: Make Optional // Shift during which the purchase order was created
  /**
   * When set, this create call is a full reversal of the referenced supplier payment.
   * The service validates that `amount_paid` is negative and equals the original's net,
   * and atomically stamps the original with `reversed_by_payment_id`.
   */
  reverses_payment_id?: string;
}

export interface GetAllPurchaseOrderPayments {
  by_merchant?: boolean;
  by_supplier?: boolean;
  by_date?: boolean;
  by_purchase_order?: boolean;
  by_store?: boolean;
  supplier_id?: string;
  cursor?: string;
  limit?: number;
  merchant_id?: string;
  purchase_order_id?: string;
  store_id?: string;
  start_date?: string;
  end_date?: string;
  fetch_all?: boolean;
}

export interface PurchaseOrderPaymentSummaries {
  merchants: PurchaseOrderPaymentMerchantSummary[];
  stores: PurchaseOrderPaymentStoreSummary[];
  suppliers: PurchaseOrderPaymentSupplierSummary[];
  purchase_orders: PurchaseOrderPaymentPurchaseOrderSummary[];
}

export interface PurchaseOrderPaymentSummary {
  total_money: number;
  total_due: number;
  total_paid: number;
  total_refunded: number;
  total_cancelled_amount: number;
  final_amount: number;
  count: number;
}

export interface PurchaseOrderPaymentMerchantSummary
  extends PurchaseOrderPaymentSummary {
  merchant_id: string;
}

export interface PurchaseOrderPaymentSupplierSummary
  extends PurchaseOrderPaymentSummary {
  supplier_id: string;
}

export interface PurchaseOrderPaymentPurchaseOrderSummary
  extends PurchaseOrderPaymentSummary {
  purchase_order_id: string;
}

export interface PurchaseOrderPaymentStoreSummary
  extends PurchaseOrderPaymentSummary {
  store_id: string;
}

export interface PurchaseOrderPaymentPage {
  purchase_order_payments: PurchaseOrderPayment[];
  cursor: string | null | undefined;
  count: number;
  summary: PurchaseOrderPaymentSummaries | null | undefined;
}

export enum PurchaseOrderEstimateStatus {
  /** The line is complete; its selling prices still have to be confirmed. */
  PENDING = 'PENDING',
  SAVED = 'SAVED',
}

/** One selling option (the parent or a fraction) of a line's estimate. */
export interface PurchaseOrderEstimateMember {
  /** Store product id of this family member. */
  id: string;
  name: string;
  quantity_in_unit_package: number;
  /** Cost per member unit, derived from the line's received cost. */
  cost: number;
  /** Selling price per member unit confirmed by the user. */
  price: number;
  /** How many member units the received stock can sell (2 dp). */
  possible_sales: number;
  /** Total profit when all received stock sells through this member. */
  profit: number;
}

/**
 * Potential gross profit of one purchase-order line, calculated once when
 * the line is complete (received ≥ ordered − cancelled). Each member sells
 * all the received stock; min / max are the lowest and highest member.
 */
export interface PurchaseOrderItemEstimate {
  status: PurchaseOrderEstimateStatus;
  /** Received quantity in line units, net of cancellations. */
  received_quantity: number;
  /** Weighted cost per line unit over every receive of the line. */
  unit_cost: number;
  members: PurchaseOrderEstimateMember[];
  min_profit: number;
  max_profit: number;
  saved_at?: string;
  saved_by_id?: string;
  saved_by_name?: string;
}

export interface SavePurchaseOrderItemEstimateMember {
  id: string;
  price: number;
}

export interface SavePurchaseOrderItemEstimate {
  merchant_id: string;
  store_id: string;
  /** Purchase order id. */
  id: string;
  item_id: string;
  /** A member left out keeps its current selling price. */
  members: SavePurchaseOrderItemEstimateMember[];
  /** Expiry date (YYYY-MM-DD) of the received stock; null clears it. */
  expiry_date?: string | null;
  updator_id: string;
}

export interface PurchaseOrder extends Timestamps, Versioned {
  id: string;
  purchase_date: string;
  ordered_by: string;
  note: string;
  supplier_id: string;
  purchase_status: PurchaseStatus;
  total_cost: number;
  items: PurchaseOrderItem[];
  total_received: number;
  payment_status: PurchaseOrderPaymentStatus;
  is_draft: boolean;
  store_id: string;
  merchant_id: string;
  total_ordered: number;
  supplier_name: string;
  total_cancelled: number;
  shift_id?: string; // TODO: Make Optional // Shift during which the purchase order was created
  po_total_paid?: number; // Cumulative amount paid (net of refunds) — source of truth
  po_total_refunded?: number; // Cumulative refund total — source of truth
  po_amount_due?: number; // Outstanding balance: (final_amount - po_total_paid) — source of truth
  cancelled_at?: string | null; // ISO 8601 timestamp when the PO was cancelled
  /** Σ of the lines' saved `estimate.min_profit`. Missing = no estimates. */
  estimate_min_profit?: number;
  /** Σ of the lines' saved `estimate.max_profit`. Missing = no estimates. */
  estimate_max_profit?: number;
  /** Lines whose estimate is still PENDING. Missing = 0. */
  estimate_pending_count?: number;
}

export interface PurchaseOrderItem extends Timestamps {
  id: string;
  quantity: number;
  cost: number;
  received: number;
  cancelled: number;
  name: string;
  in_stock: number;
  received_at?: string;
  fractions?: PurchaseOrderItemFraction[];
  /** Optional for backwards compatibility; missing = no estimate. */
  estimate?: PurchaseOrderItemEstimate | null;
  /**
   * Expiry date (YYYY-MM-DD) of the stock received on this line, set when
   * the line's selling prices are confirmed. Optional; missing = unknown.
   */
  expiry_date?: string | null;
}

export interface UpdatePurchaseOrderItem {
  quantity: number;
  id: string;
  purchase_cost?: number;
  fractions?: PurchaseOrderItemFraction[];
}

export interface PurchaseOrderItemFraction {
  id: string;
  parent_id: string;
  quantity: number;
  purchase_cost: number;
  fraction: number;
  name: string;
  received: number;
  cancelled: number;
}

export interface ReceivePurchaseOrderItemFraction {
  quantity: number;
  id: string;
  parent_id: string;
  fraction: number;
}

export interface ReceivePurchaseOrderItem {
  quantity: number;
  id: string;
  cost: number;
  fractions?: ReceivePurchaseOrderItemFraction[];
}

export interface CreatePurchaseOrder {
  purchase_date: string;
  ordered_by: string;
  note: string;
  supplier_id: string;
  items: UpdatePurchaseOrderItem[];
  store_id: string;
  merchant_id: string;
  shift_id?: string; // TODO: Make Optional // Shift during which the purchase order was created
  /**
   * Makes a retried create a no-op: the first create with this key makes
   * the PO, a repeat returns that PO (POS quick purchase orders send their
   * op id). Optional for backwards compatibility; missing = every request
   * creates a PO.
   */
  idempotency_key?: string;
}

export enum UpdatePurchaseOrderCommand {
  ADD = 'ADD',
  REMOVE = 'REMOVE',
  CANCEL = 'CANCEL',
  COMMENT = 'COMMENT',
  CANCEL_ITEMS = 'CANCEL_ITEMS',
  REFUND_ITEM = 'REFUND_ITEM',
}

export interface RefundItem {
  id: string;
  amount: number;
  merchant_id: string;
  store_id: string;
  updator_id: string;
}

export interface UpdatePurchaseOrder {
  store_id: string;
  id: string;
  items: UpdatePurchaseOrderItem[];
  command: UpdatePurchaseOrderCommand;
  cancellations?: CancelPurchaseOrderItem[];
  refunds?: RefundItem[];
  updator_id: string;
  note?: string;
  shift_id?: string; // TODO: Make Optional // Shift during which the purchase order was updated
}

export interface ReceivePurchaseOrder {
  // purchase order id
  id: string;
  items: ReceivePurchaseOrderItem[];
  creator_id: string;
  store_id: string;
  shift_id?: string; // TODO: Make Optional // Shift during which the purchase order was received
  /**
   * When the goods arrived (ISO 8601), for a receive made offline and
   * uploaded later (the POS quick purchase order). Every ledger of the
   * receive is dated at it, so a later offline count does not add it
   * twice. At most 35 days old and 5 minutes ahead.
   * Optional for backwards compatibility; missing = now.
   */
  received_at?: string;
}

export interface PurchaseOrderPage {
  purchase_orders: PurchaseOrder[];
  cursor: string | null | undefined;
  count: number;
}

export interface GetAllPurchaseOrders {
  by_merchant?: boolean;
  by_supplier?: boolean;
  by_date?: boolean;
  by_payment_status?: boolean;
  by_purchase_status?: boolean;
  by_payment_status_and_date?: boolean;
  by_purchase_status_and_date?: boolean;
  by_store?: boolean;
  by_store_and_date?: boolean;
  supplier_id?: string;
  merchant_id: string;
  cursor?: string;
  purchase_status?: PurchaseStatus;
  payment_status?: PurchaseOrderPaymentStatus;
  store_id?: string;
  start_date?: string;
  end_date?: string;
  limit?: number;
}

export interface GetPurchaseOrder {
  id: string;
}

export interface DeletePurchaseOrder {
  id: string;
}

export interface PurchaseOrderPaymentDBItem
  extends PurchaseOrderPayment,
    DynamoDBItem {}

export interface ReconcilePaymentFieldsResponse {
  updated_count: number;
  errors: string[];
}

export interface SupplierValuationPeriodMetrics {
  total_money: number;
  total_cancelled_amount: number;
  final_amount: number;
  total_paid: number;
  total_refunded: number;
  total_due: number;
}

export interface SupplierValuation {
  supplier_due_start: number;
  supplier_due_end: number;
  period_metrics: SupplierValuationPeriodMetrics;
  period_metrics_delta: SupplierValuationPeriodMetrics;
  per_supplier?: SupplierValuationPerSupplier[];
}

export interface SupplierValuationPerSupplier {
  supplier_id: string;
  supplier_name?: string;
  supplier_due_start: number;
  supplier_due_end: number;
  /**
   * Metrics for this supplier at end_date, same shape and meaning as
   * `period_metrics`. `total_due` is the receive-based due and always equals
   * `supplier_due_end` — it is repeated so a single card component can render
   * a supplier row and a store row from the same field names.
   */
  total_money: number;
  total_cancelled_amount: number;
  final_amount: number;
  total_paid: number;
  total_refunded: number;
  total_due: number;
  /**
   * Gross paid to this supplier BETWEEN start_date and end_date, unlike
   * `total_paid`, which is everything paid up to end_date. This is the figure
   * a date-ranged report shows beside the supplier's opening and closing due.
   */
  paid_in_period: number;
  /** Number of purchase orders behind these numbers. */
  count: number;
}

export interface GetSupplierValuation {
  merchant_id: string;
  store_id: string;
  start_date: string;
  end_date: string;
}

// ── PO Payment Snapshot Types ──

export interface POPaymentStoreSnapshot {
  id: string;
  store_id: string;
  merchant_id: string;
  supplier_id: string;
  po_id: string;
  total_money: number;
  total_cancelled_amount: number;
  final_amount: number;
  total_paid: number;
  total_refunded: number;
  total_due: number;
  created_at: string;
  updated_at: string;
}

export interface GetStoreSnapshotAtTime {
  store_id: string;
  datetime: string;
  po_id?: string;
  supplier_id?: string;
}

export interface GetSupplierSnapshotsInRange {
  supplier_id: string;
  start_date: string;
  end_date: string;
  po_id?: string;
  limit?: number;
  cursor?: string;
}

export interface GetSupplierSnapshotAtTime {
  supplier_id: string;
  datetime: string;
  po_id?: string;
}

export interface GetStoreSnapshotsInRange {
  store_id: string;
  start_date: string;
  end_date: string;
  po_id?: string;
  supplier_id?: string;
  limit?: number;
  cursor?: string;
}

export interface POPaymentSupplierSnapshot {
  id: string;
  store_id: string;
  merchant_id: string;
  supplier_id: string;
  po_id: string;
  total_money: number;
  total_cancelled_amount: number;
  final_amount: number;
  total_paid: number;
  total_refunded: number;
  total_due: number;
  created_at: string;
  updated_at: string;
}

export interface POPaymentStoreSnapshotDBItem
  extends POPaymentStoreSnapshot,
    DynamoDBItem {}

export interface POPaymentSupplierSnapshotDBItem
  extends POPaymentSupplierSnapshot,
    DynamoDBItem {}

export interface POPaymentStoreSnapshotPage {
  snapshots: POPaymentStoreSnapshot[];
  cursor: string | null | undefined;
  count: number;
}

export interface POPaymentSupplierSnapshotPage {
  snapshots: POPaymentSupplierSnapshot[];
  cursor: string | null | undefined;
  count: number;
}

// ── PO Receive Event Types ──

/**
 * Immutable ledger record of goods received against a purchase order — one
 * per item per receive action. Together with the payment ledger it derives
 * supplier due at any point in time:
 *   due(T) = Σ receive-event value ≤ T − paid(T) + refunded(T)
 * so a PO owes nothing before delivery and each receive offsets exactly the
 * inventory value it adds.
 */
export interface PurchaseOrderReceiveEvent {
  id: string;
  merchant_id: string;
  store_id: string;
  supplier_id: string;
  po_id: string;
  item_id: string;
  product_name: string;
  /** Quantity received in this action, in parent units. */
  quantity_received: number;
  /**
   * Weighted per-parent-unit cost of this receive (fraction purchase costs
   * folded in), so `value = cost × quantity_received` holds.
   */
  cost: number;
  /**
   * Inventory value added by this receive (receive cost × quantity, matching
   * the PURCHASE_ORDER inventory change), which is also the supplier-due
   * value this receive recognizes.
   */
  value: number;
  creator_id: string;
  creator_name: string;
  shift_id?: string;
  created_at: string;
  updated_at: string;
}

export interface PurchaseOrderReceiveEventDBItem
  extends PurchaseOrderReceiveEvent,
    DynamoDBItem {}

export interface PurchaseOrderReceiveEventPage {
  receive_events: PurchaseOrderReceiveEvent[];
  cursor: string | null | undefined;
  count: number;
}

export interface GetPurchaseOrderReceiveEvents {
  merchant_id: string;
  by_po?: boolean;
  by_store_and_date_range?: boolean;
  po_id?: string;
  store_id?: string;
  start_date?: string;
  end_date?: string;
  limit?: number;
  cursor?: string;
}

export interface GetSnapshotAtTime {
  merchant_id: string;
  store_id?: string;
  supplier_id?: string;
  datetime: string;
  po_id?: string;
}

export interface GetSnapshotsInRange {
  merchant_id: string;
  store_id?: string;
  supplier_id?: string;
  start_date: string;
  end_date: string;
  po_id?: string;
}

// ── Backfill PO Payment Snapshots ──

export interface BackfillPOPaymentSnapshotsRequest {
  purchase_order_id: string;
  store_id: string;
}

export interface PaymentSnapshotBackfillResult {
  payment_id: string;
  payment_created_at: string;
  reason?: string;
}

export interface BackfillPOPaymentSnapshotsResponse {
  po_id: string;
  store_id: string;
  supplier_id: string;
  total_payments: number;
  created: PaymentSnapshotBackfillResult[];
  skipped: PaymentSnapshotBackfillResult[];
  failed: PaymentSnapshotBackfillResult[];
  validation_warning?: string;
}
